import React, { memo } from "react";
import { connect } from "react-redux";
import _get from "lodash/get";
import getConfig from "next/config";
import LazyLoad from "react-lazyload";
import Link from "next/link";
import classNames from "classnames";
import Status from "@components/Status";
import { productHelper, resizeImage } from "@lib/helpers";
import ProductCardLabel from "@components/ProductCardLabel";
import Price from "@components/Price";
import Ratings from "@components/Ratings";
import { trackProductClick } from "@store/tracker/actions";
import AddToCart from "@containers/Catalog/Actions/AddToCart";
import AddToCompare from "@containers/Catalog/Actions/AddToCompare";
import AddToWishlist from "@containers/Catalog/Actions/AddToWishlist";

const {
  publicRuntimeConfig: { settings },
} = getConfig();

const isBricoStore = settings.websiteName === "bricostore";

// Brico: offer label top-left; favorite/compare top-RIGHT and HIGH (George). The label's
// right edge (right-[52px]) stops before the ~32px buttons so it never runs under them.
const cornerLabelPos = isBricoStore
  ? "top-10px left-11px right-[52px] lg:top-15px lg:left-16px items-start"
  : "top-0 right-0 items-end";
const actionsPos = isBricoStore
  ? "top-10px right-11px lg:top-15px lg:right-16px"
  : "top-10px left-11px lg:top-15px lg:left-16px";
// Brico: don't clip the ribbon point/eyelet + small gap between labels (width is bounded
// by the left/right positions above, so a long label truncates before the buttons;
// max-w-[85%] kept as a safety cap, matching the category card).
const cornerLabelBox = isBricoStore
  ? "overflow-visible gap-1 max-w-[85%]"
  : "truncate max-w-[75%]";

const LinkWrapper = ({ children, href, ...props }) => {
  if (href) {
    return (
      <Link href={href}>
        <a {...props}>{children}</a>
      </Link>
    );
  } else {
    return <div {...props}>{children}</div>;
  }
};

const Product = ({ product, isTeaser }) => {
  const { isDisabled, isWestaco, getLabels, hasPricePerFactor, hasPriceUnit } =
    productHelper(product);

  return (
    <div
      data-test="PRODUCT_CARD"
      className={classNames(
        "Product flex flex-col relative bg-white max-h-[490px]",
        {
          "hover:shadow-mixRedYellow": !isTeaser,
          "shadow-raised sm:hover:shadow-raised-hover border-0 rounded-[8px]":
            !isTeaser && settings.websiteName === "bricostore",
        }
      )}
    >
      <LinkWrapper
        href={isTeaser ? null : `/${product.url}/cpd/${product.sku}/`}
        title=""
        className="block Product-photoWrapper min-w-[100px] max-w-[250px] mx-auto mt-0"
      >
        <LazyLoad
          once
          debounce={40}
          offset={400}
          height={120}
          className="Product-photo mx-auto"
          placeholder={
            <img
              src={`${settings.websiteStatics}/images/product-image-placeholder.png`}
              // If we have 500 products on page,
              // adding alt increases page size by a whole lot
              alt=""
              className="Product-photo mx-auto mt-3 lg:mt-5 w-100px sm:w-120px mb-4 lg:mb-6"
            />
          }
        >
          <img
            src={
              (product.image.startsWith("/resize/") &&
                `${settings.cdn}${product.image}`) ||
              resizeImage(product.image, "300x300")
            }
            // If we have 500 products on page,
            // adding alt increases page size by a whole lot
            alt=""
            className={`Product-photo mx-auto${
              isBricoStore ? " rounded-t-[8px]" : ""
            }`}
          />
        </LazyLoad>
      </LinkWrapper>

      {/* Brico: stock badge sits above the product name (mirrors the category card) */}
      {isBricoStore && !isTeaser && !isDisabled && (
        <div className="mt-[6px] mb-[2px]">
          <Status product={product} />
        </div>
      )}

      <LinkWrapper
        href={isTeaser ? null : `/${product.url}/cpd/${product.sku}/`}
        title=""
      >
        <span
          className={classNames(
            "Product-nameHeading leading-20 text-sm truncate-3-lines mt-2 min-h-[57px]",
            {
              "hover:text-red-brand": !isTeaser,
            }
          )}
          data-test="PRODUCT_CARD_NAME"
        >
          {product.name}
        </span>
      </LinkWrapper>

      {!isDisabled && (
        <>
          {isWestaco && product.price && <div>{`De la`}</div>}

          {getLabels().length !== 0 && (
            <div
              className={`absolute ${cornerLabelPos} h-auto w-auto flex flex-col ${cornerLabelBox}`}
            >
              {/* Brico: drop the empty slot getLabels() appends (keep any real labels) */}
              {(isBricoStore
                ? getLabels().filter((label) => label.title)
                : getLabels()
              ).map((label, index) => (
                <ProductCardLabel
                  key={`${label.title}-${index}`}
                  variant={label.variant}
                  className="truncate max-w-full"
                >
                  {label.title}
                </ProductCardLabel>
              ))}
            </div>
          )}

          {!isBricoStore && !isTeaser && <Status product={product} />}

          <div className="h-[20px]">
            <Ratings
              reviewsCount={_get(product, "reviews_count", 0)}
              ratingSummary={_get(product, "reviews_value", null)}
              displayReviewsNo={true}
              productUrl={`/${product.url}/cpd/${product.sku}/`}
            />
          </div>

          <div className="mt-auto flex items-end">
            <Price
              productId={parseInt(product.id)}
              price={product.final_price}
              regularPrice={product.price}
              formatted={true}
              discountType={product.discount_type}
              displayDiscount={true}
              size="smaller"
              displayUnit={
                hasPricePerFactor && hasPriceUnit
                  ? product.price_unit.split("/")[1]
                  : false
              }
            />
          </div>
          {!isTeaser && (
            <AddToCart
              product={product}
              isCatalog
              noMargin
              titleOnlyOnMobile={settings.websiteName === "bricostore"}
            />
          )}
        </>
      )}

      <div
        className={`absolute ${actionsPos} flex flex-col justify-center items-center space-y-1.5`}
      >
        <AddToWishlist product={product} />
        {!isTeaser && <AddToCompare product={product} />}
      </div>
    </div>
  );
};

export default connect(null, {
  trackProductClick,
})(memo(Product));
